import { db } from '../firebase';
import {
  collection, doc, setDoc, addDoc, getDoc, deleteDoc,
  query, where, orderBy, onSnapshot, updateDoc,
  serverTimestamp, increment, deleteField, getDocs,
} from 'firebase/firestore';
import { getPrimarySavedPoster } from '../utils/primaryCollage';
import { normalizeSavedPostersList } from '../utils/savedPosterNormalize';
import { loadAcceptedDailyVibePartnerUids } from '../utils/dailyVibeFirestore';

const getConversationId = (uid1, uid2) => [uid1, uid2].sort().join('_');

export const getOrCreateConversation = async (currentUser, targetUserId, { senderDisplayName, targetDisplayName, targetEmail } = {}) => {
  const conversationId = getConversationId(currentUser.uid, targetUserId);
  const conversationRef = doc(db, 'conversations', conversationId);

  let exists = false;
  try {
    const conversationSnap = await getDoc(conversationRef);
    exists = conversationSnap.exists();
  } catch (_) {
    exists = false;
  }

  if (!exists) {
    const senderDetail = { displayName: senderDisplayName || 'Someone' };
    if (currentUser.email) senderDetail.email = currentUser.email;

    const targetDetail = { displayName: targetDisplayName || 'Someone' };
    if (targetEmail) targetDetail.email = targetEmail;

    await setDoc(conversationRef, {
      participants: [currentUser.uid, targetUserId],
      participantDetails: {
        [currentUser.uid]: senderDetail,
        [targetUserId]: targetDetail,
      },
      lastMessage: null,
      unreadCount: { [currentUser.uid]: 0, [targetUserId]: 0 },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  return conversationId;
};

/** Stored on daily-vibe-share invite system messages (see Inbox display helpers). */
export const DAILY_VIBE_SHARE_INVITE_TEXT = 'wants to share daily vibe checks with you!';

export function isDailyVibeShareInviteMessage(msg) {
  if (!msg || typeof msg !== 'object') return false;
  if (msg.dailyVibeShareInvite === true) return true;
  const t = typeof msg.text === 'string' ? msg.text.trim().toLowerCase() : '';
  return t === DAILY_VIBE_SHARE_INVITE_TEXT;
}

export function formatDailyVibeShareInviteDisplay({ isSender, senderName }) {
  if (isSender) return 'You invited them to share daily vibe checks!';
  const name = senderName && String(senderName).trim() ? String(senderName).trim() : 'Someone';
  return `${name} wants to share daily vibe checks with you!`;
}

export const DAILY_VIBE_SHARE_ACCEPTED_TEXT = 'accepted to share daily vibe checks!';
export const DAILY_VIBE_SHARE_DECLINED_TEXT = 'declined to share daily vibe checks.';

/** Preview / bubble label for a daily vibe-check GIF shared into a 1:1 chat. */
export const DAILY_VIBE_CHECK_GIF_TEXT = 'vibe check gif';

export function isDailyVibeShareResponseMessage(msg) {
  return !!msg && typeof msg === 'object'
    && (msg.dailyVibeShareAccepted === true || msg.dailyVibeShareDeclined === true);
}

export function formatDailyVibeShareResponseDisplay({ msg, isSender, senderName }) {
  const name = senderName && String(senderName).trim() ? String(senderName).trim() : 'Someone';
  if (msg.dailyVibeShareAccepted === true) {
    return isSender
      ? "You're now sharing daily vibe checks!"
      : `${name} accepted — you're now sharing daily vibe checks!`;
  }
  return isSender
    ? 'You declined to share daily vibe checks.'
    : `${name} declined to share daily vibe checks.`;
}

export function isDailyVibeCheckGifMessage(msg) {
  if (!msg || typeof msg !== 'object') return false;
  if (msg.dailyVibeCheckGif === true) return true;
  const t = typeof msg.text === 'string' ? msg.text.trim().toLowerCase() : '';
  return Boolean(msg.gifUrl) && t === DAILY_VIBE_CHECK_GIF_TEXT;
}

export const sendMessage = async (conversationId, senderId, text, {
  gifUrl,
  isSystemMessage,
  dailyVibeShareInvite,
  dailyVibeShareAccepted,
  dailyVibeShareDeclined,
  dailyVibeCheckGif,
} = {}) => {
  const messagesRef = collection(db, 'conversations', conversationId, 'messages');
  const conversationRef = doc(db, 'conversations', conversationId);

  const conversationSnap = await getDoc(conversationRef);
  const conversation = conversationSnap.data();
  const recipientId = conversation.participants.find(id => id !== senderId);

  const messageData = {
    senderId,
    text,
    timestamp: serverTimestamp(),
    source: 'platform',
  };
  if (gifUrl) messageData.gifUrl = gifUrl;
  if (isSystemMessage) messageData.isSystemMessage = true;
  if (dailyVibeShareInvite) messageData.dailyVibeShareInvite = true;
  if (dailyVibeShareAccepted) messageData.dailyVibeShareAccepted = true;
  if (dailyVibeShareDeclined) messageData.dailyVibeShareDeclined = true;
  if (dailyVibeCheckGif) messageData.dailyVibeCheckGif = true;

  await addDoc(messagesRef, messageData);

  const displayText = dailyVibeCheckGif
    ? DAILY_VIBE_CHECK_GIF_TEXT
    : (gifUrl ? (text || 'Sent a GIF') : text);
  const updates = {
    lastMessage: {
      text: displayText,
      senderId,
      timestamp: new Date(),
      ...(dailyVibeCheckGif ? { dailyVibeCheckGif: true } : {}),
      ...(gifUrl ? { gifUrl } : {}),
    },
    updatedAt: serverTimestamp(),
  };
  if (recipientId) {
    updates[`unreadCount.${recipientId}`] = increment(1);
  }
  await updateDoc(conversationRef, updates);
};

/**
 * After the user completes today's vibe check, post their GIF into each accepted
 * partner's 1:1 conversation (marked as a vibe-check GIF). Best-effort per partner.
 */
export const shareDailyVibeCheckGifWithPartners = async (currentUser, gifUrl) => {
  if (!currentUser?.uid || !gifUrl) return;
  let partnerUids = [];
  try {
    partnerUids = await loadAcceptedDailyVibePartnerUids(db, currentUser.uid);
  } catch (err) {
    console.warn('Failed to load vibe-share partners for GIF share:', err);
    return;
  }
  if (!partnerUids.length) return;

  await Promise.all(
    partnerUids.map(async (otherUid) => {
      try {
        const conversationId = await getOrCreateConversation(currentUser, otherUid);
        await sendMessage(conversationId, currentUser.uid, DAILY_VIBE_CHECK_GIF_TEXT, {
          gifUrl,
          dailyVibeCheckGif: true,
        });
      } catch (err) {
        console.warn('Failed to share daily vibe GIF with partner:', otherUid, err);
      }
    }),
  );
};

export const subscribeToConversations = (userId, callback) => {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId),
    orderBy('updatedAt', 'desc'),
  );

  return onSnapshot(q, (snapshot) => {
    const conversations = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(conversations);
  });
};

export const subscribeToMessages = (conversationId, callback) => {
  const q = query(
    collection(db, 'conversations', conversationId, 'messages'),
    orderBy('timestamp', 'asc'),
  );

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(messages);
  });
};

export const markAsRead = async (conversationId, userId) => {
  const conversationRef = doc(db, 'conversations', conversationId);
  await updateDoc(conversationRef, { [`unreadCount.${userId}`]: 0 });
};

export const requestUnmask = async (conversationId, userId) => {
  const userSnap = await getDoc(doc(db, 'users', userId));
  const data = userSnap.data();
  const trimUrl = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');
  let originalPhotoUrl = trimUrl(data?.userPhotoOriginalUrl);
  if (!originalPhotoUrl) {
    const posters = normalizeSavedPostersList(Array.isArray(data?.savedWantedPosters) ? data.savedWantedPosters : []);
    const primary = getPrimarySavedPoster(posters, data?.primaryCollageId ?? null);
    originalPhotoUrl = trimUrl(primary?.userPhotoOriginalUrl);
  }
  if (!originalPhotoUrl) {
    throw new Error('No original photo found. Please upload a photo first.');
  }

  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationSnap = await getDoc(conversationRef);
  const conversation = conversationSnap.data();
  const participants = Array.isArray(conversation?.participants) ? conversation.participants : [];
  const otherId = participants.find((id) => id !== userId);
  const otherAlreadyUnmasked = Boolean(
    otherId && conversation?.unmask?.[otherId]?.photoUrl,
  );

  await updateDoc(conversationRef, {
    [`unmask.${userId}`]: {
      photoUrl: originalPhotoUrl,
      requestedAt: serverTimestamp(),
    },
  });

  const systemMessageText = otherAlreadyUnmasked
    ? "You're both unmasked. Go ahead and say hi! 👀"
    : 'wants to unmask! 👀';

  await sendMessage(conversationId, userId, systemMessageText, { isSystemMessage: true });
};

export const cancelUnmask = async (conversationId, userId) => {
  const conversationRef = doc(db, 'conversations', conversationId);
  await updateDoc(conversationRef, {
    [`unmask.${userId}`]: deleteField(),
  });
  await sendMessage(conversationId, userId, 'cancelled the unmask request', { isSystemMessage: true });
};

export const deleteConversation = async (conversationId) => {
  const messagesSnap = await getDocs(collection(db, 'conversations', conversationId, 'messages'));
  const deletePromises = messagesSnap.docs.map(d => deleteDoc(d.ref));
  await Promise.all(deletePromises);
  await deleteDoc(doc(db, 'conversations', conversationId));
};

export const getTotalUnreadCount = (userId, callback) => {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId),
  );

  return onSnapshot(q, (snapshot) => {
    let total = 0;
    snapshot.docs.forEach(d => {
      total += d.data().unreadCount?.[userId] || 0;
    });
    callback(total);
  });
};
